import { motion } from "framer-motion";
import { Crown, Sparkles, Trophy, Star, Award, Circle } from "lucide-react";

/**
 * Canonical rarity metadata mirroring backend/pack_rarity.RARITY_META.
 * Used by RarityBadge, PublishWizard, ExplorePackCard.
 */
export const RARITY = {
  common:    { label: "Common",    color: "#9CA3AF", glow: "rgba(156,163,175,0.40)", suggested: 0, max: 2,  Icon: Circle,   rank: 0 },
  uncommon:  { label: "Uncommon",  color: "#34D399", glow: "rgba(52,211,153,0.55)",  suggested: 1, max: 3,  Icon: Award,    rank: 1 },
  rare:      { label: "Rare",      color: "#5AC8FA", glow: "rgba(90,200,250,0.55)",  suggested: 2, max: 5,  Icon: Star,     rank: 2 },
  epic:      { label: "Epic",      color: "#B8A4FF", glow: "rgba(184,164,255,0.65)", suggested: 4, max: 7,  Icon: Sparkles, rank: 3 },
  legendary: { label: "Legendary", color: "#FCD34D", glow: "rgba(252,211,77,0.70)",  suggested: 6, max: 9,  Icon: Trophy,   rank: 4 },
  mythic:    { label: "Mythic",    color: "#FCA5A5", glow: "rgba(252,165,165,0.85)", suggested: 8, max: 10, Icon: Crown,    rank: 5 },
};

export function rarityMeta(rarity) {
  return RARITY[(rarity || "common").toLowerCase()] || RARITY.common;
}

/**
 * Small pill badge for compact spots (tile corners, study list rows).
 * Mythic gets a holographic shimmer; legendary gets a soft pulse.
 */
export default function RarityBadge({ rarity, size = "sm", className = "", showIcon = true, testid }) {
  const r = (rarity || "common").toLowerCase();
  const meta = rarityMeta(r);
  const Icon = meta.Icon;

  const dims = size === "xs"
    ? { px: "px-1.5 py-0.5", text: "text-[9.5px]", icon: 9 }
    : size === "sm"
    ? { px: "px-2 py-0.5", text: "text-[10.5px]", icon: 11 }
    : { px: "px-2.5 py-1", text: "text-[11.5px]", icon: 12 };

  return (
    <span
      data-testid={testid || `rarity-${r}`}
      className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-[0.14em] ${dims.px} ${dims.text} ${className}`}
      style={{
        color: meta.color,
        background: `${meta.color}14`,
        border: `1px solid ${meta.color}55`,
        boxShadow: r === "mythic"
          ? `0 0 14px -2px ${meta.glow}, inset 0 0 10px -4px ${meta.glow}`
          : r === "legendary"
          ? `0 0 12px -2px ${meta.glow}`
          : "none",
      }}
    >
      {showIcon && <Icon size={dims.icon} strokeWidth={2.4} />}
      {meta.label}
    </span>
  );
}

/** Big animated tier reveal — used inside the publish wizard. */
export function RarityReveal({ rarity, reason }) {
  const meta = rarityMeta(rarity);
  const Icon = meta.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, type: "spring", stiffness: 220 }}
      className="text-center py-2"
      data-testid={`rarity-reveal-${(rarity || "common").toLowerCase()}`}
    >
      <motion.div
        className="mx-auto w-24 h-24 rounded-3xl flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${meta.color}30, ${meta.color}10)`,
          boxShadow: `0 0 60px -8px ${meta.glow}, inset 0 0 0 1.5px ${meta.color}66`,
        }}
        animate={{
          boxShadow: [
            `0 0 60px -8px ${meta.glow}, inset 0 0 0 1.5px ${meta.color}66`,
            `0 0 80px -4px ${meta.glow}, inset 0 0 0 1.5px ${meta.color}aa`,
            `0 0 60px -8px ${meta.glow}, inset 0 0 0 1.5px ${meta.color}66`,
          ],
        }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Icon size={42} style={{ color: meta.color, filter: `drop-shadow(0 0 12px ${meta.color})` }} strokeWidth={1.8} />
      </motion.div>
      <div className="mt-5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#8E8B99]">PACK RARITY</div>
      <div
        className="text-[34px] font-bold tracking-tight mt-1 mb-2"
        style={{ color: meta.color, textShadow: `0 0 22px ${meta.color}66` }}
      >
        {meta.label}
      </div>
      {reason && (
        <p className="text-[13px] text-[#B0AEBC] max-w-md mx-auto leading-snug">
          {reason}
        </p>
      )}
    </motion.div>
  );
}
